import { Container, IInit } from 'common/container/Container'
import { CalendarContainerConfig } from 'modules/calendar/container'
import { Observable, of } from 'rxjs'
import { Query } from 'common/api/Query'
import { HTTP_CLIENT_KEY, IHTTPClient } from 'common/api/HTTPClient'
import { IStatusService } from 'common/status/StatusService'
import { catchError, map } from 'rxjs/operators'
import { emptyList, ItemList } from 'common/models/ItemList'
import { prepareURL } from 'common/api/http-helpers'
import { STATUS_SERVICE_KEY } from 'container/app'
import {
  ExternAppointment,
  ExternAppointmentQuery,
} from 'modules/calendar/models/ExternAppointment'
import { ExternAppointmentDTO, toModel } from 'modules/calendar/models/ExternAppointmentDTO'
import { ExternAppointmentAction } from '../enums/ExternAppointmentAction'

export interface IExternAppointmentApi extends IInit {
  getByID(id: string): Observable<ExternAppointment | undefined>

  getFilteredList(q: Query<ExternAppointmentQuery>): Observable<ItemList<ExternAppointment>>

  add(e: ExternAppointmentDTO): Observable<ExternAppointment | undefined>

  update(e: ExternAppointmentDTO): Observable<ExternAppointment | undefined>

  delete(id: string): Observable<boolean>

  modifyAppointmentStatus(
    id: string,
    action: ExternAppointmentAction,
    cancelMessage: string | undefined,
    cancelUserId: string | undefined
  ): Observable<boolean>
}

export class ExternAppointmentApi implements IExternAppointmentApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url =
      (this._container.config as CalendarContainerConfig).moduleFullUrl + '/extern-appointments'
  }

  add(e: ExternAppointmentDTO): Observable<ExternAppointment | undefined> {
    return this._httpClient.post<ExternAppointment>({ url: this._url, body: e }).pipe(
      map<ExternAppointmentDTO, ExternAppointment>((d) => {
        this._statusService.sendStatus({ variant: 'success' })
        return toModel(d)
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: this._url + '/' + id }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getByID(id: string): Observable<ExternAppointment | undefined> {
    return this._httpClient.get<ExternAppointment>({ url: `${this._url}/${id}` }).pipe(
      map<ExternAppointmentDTO, ExternAppointment>((d) => toModel(d)),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  getFilteredList(q: Query<ExternAppointmentQuery>): Observable<ItemList<ExternAppointment>> {
    return this._httpClient
      .get<ItemList<ExternAppointment>>({ url: prepareURL(`${this._url}`, q) })
      .pipe(
        map<ItemList<ExternAppointmentDTO>, ItemList<ExternAppointment>>((dto) => {
          const itemList = emptyList<ExternAppointment>()
          itemList.count = dto.count
          itemList.items = dto.items.map((d) => toModel(d))
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<ExternAppointment>())
        })
      )
  }

  update(e: ExternAppointmentDTO): Observable<ExternAppointment | undefined> {
    return this._httpClient.put<ExternAppointment>({ url: this._url, body: e }).pipe(
      map<ExternAppointmentDTO, ExternAppointment>((d) => {
        this._statusService.sendStatus({ variant: 'success' })
        return toModel(d)
      }),
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(undefined)
      })
    )
  }

  modifyAppointmentStatus(
    id: string,
    action: ExternAppointmentAction,
    cancelMessage: string | undefined,
    cancelUserId: string | undefined
  ): Observable<boolean> {
    return this._httpClient
      .put<boolean>({
        url: `${this._url}/${id}`,
        body: { action: action, message: cancelMessage, userId: cancelUserId },
      })
      .pipe(
        map(() => {
          return true
        }),
        catchError(() => {
          return of(false)
        })
      )
  }
}
